'use client';

import * as React from 'react';
import { Column } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * DataTableColumnHeader Props
 *
 * Props for the DataTableColumnHeader component.
 */
export interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The column object from TanStack Table */
  column: Column<TData, TValue>;
  /** The title to display for the column */
  title: string;
}

/**
 * DataTableColumnHeader Component
 *
 * A column header component for data tables that supports sorting functionality.
 * Displays the column title and sort indicators, with click-to-toggle sorting.
 *
 * Features:
 * - Visual sort indicators (↑ ascending, ↓ descending, ⇅ unsorted)
 * - Click to toggle sort (none → asc → desc → none)
 * - Dropdown menu for sort options
 * - Keyboard accessible (Enter/Space to sort)
 * - Supports non-sortable columns (displays title only)
 * - Dark mode support
 *
 * @example
 * ```tsx
 * // Sortable column
 * <DataTableColumnHeader column={column} title="Name" />
 *
 * // Non-sortable column
 * <DataTableColumnHeader column={column} title="Actions" />
 * ```
 */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>) {
  // Check if the column can be sorted
  const canSort = column.getCanSort();

  // If the column cannot be sorted, just display the title
  if (!canSort) {
    return (
      <div className={cn('flex items-center space-x-2', className)}>
        <span className="text-sm font-medium text-text-primary">{title}</span>
      </div>
    );
  }

  // Get current sort direction
  const isSorted = column.getIsSorted();

  // Render sort icon based on current sort state
  const SortIcon = () => {
    if (isSorted === 'asc') {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    if (isSorted === 'desc') {
      return <ArrowDown className="ml-2 h-4 w-4" />;
    }
    return <ChevronsUpDown className="ml-2 h-4 w-4" />;
  };

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 data-[state=open]:bg-bg-hover"
            aria-label={`Sort by ${title}`}
          >
            <span className="text-sm font-medium">{title}</span>
            <SortIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onClick={() => column.toggleSorting(false)}
            className="cursor-pointer"
          >
            <ArrowUp className="mr-2 h-3.5 w-3.5 text-text-tertiary" />
            Asc
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => column.toggleSorting(true)}
            className="cursor-pointer"
          >
            <ArrowDown className="mr-2 h-3.5 w-3.5 text-text-tertiary" />
            Desc
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => column.clearSorting()}
            className="cursor-pointer"
          >
            <ChevronsUpDown className="mr-2 h-3.5 w-3.5 text-text-tertiary" />
            Clear Sort
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
